import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listConversations } from "@/lib/social";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { MessagesSquare } from "lucide-react";
import Avatar from "@/components/Avatar";

function timeAgo(iso) {
  const d = new Date(iso); const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const dy = Math.floor(h / 24);
  if (dy < 7) return `${dy}d`;
  return d.toLocaleDateString();
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user?.id) return;
    try {
      const data = await listConversations(user.id);
      setItems(data);
    } catch (err) {
      console.error("[messages] load failed", err);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // realtime: any incoming msg or update on my outgoing msg → reload list
  useEffect(() => {
    if (!user?.id) return;
    const ch = supabase
      .channel(`msgs-list-${user.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "messages", filter: `recipient_id=eq.${user.id}` },
        () => load())
      .on("postgres_changes",
        { event: "*", schema: "public", table: "messages", filter: `sender_id=eq.${user.id}` },
        () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20">
        <span className="text-xs uppercase tracking-[0.3em] text-ink">Inbox</span>
        <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-2 leading-none mb-10">
          Messages.
        </h1>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="border border-ink/15 h-20 bg-[#101010] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-ink/15 p-12 text-center">
            <MessagesSquare size={28} strokeWidth={1.5} className="mx-auto text-ink/30 mb-4" />
            <p className="font-display text-2xl text-ink">No messages yet.</p>
            <p className="text-ink/60 mt-2">Open someone's profile and tap Message.</p>
          </div>
        ) : (
          <div data-testid="msgs-list" className="border border-ink/15 divide-y divide-border">
            {items.map((c) => (
              <Link key={c.counterpart_id} to={`/messages/${c.counterpart_id}`} data-testid={`thread-link-${c.counterpart_id}`}
                className={`flex items-center gap-4 px-4 md:px-5 py-4 transition-colors hover:bg-paper/5 ${c.unread ? "bg-paper/[0.03]" : ""}`}>
                <Avatar src={c.counterpart_avatar_url} name={c.counterpart_name} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className={`truncate ${c.unread ? "text-ink font-semibold" : "text-ink/80"}`}>
                      {c.counterpart_name}
                      {c.counterpart_username && (
                        <span className="text-ink/40 font-normal ml-1.5 text-xs">@{c.counterpart_username}</span>
                      )}
                    </p>
                    <span className="text-[10px] uppercase tracking-widest text-ink/40 shrink-0">{timeAgo(c.last_at)}</span>
                  </div>
                  <p className={`text-sm truncate mt-0.5 ${c.unread ? "text-ink/80" : "text-ink/50"}`}>
                    {c.last_message}
                  </p>
                </div>
                {c.unread && <span className="w-2 h-2 bg-neon shrink-0" />}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
